import React, {
  useMemo,
  useState,
  ChangeEvent,
  MouseEvent,
  useEffect,
} from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  InputBase,
  IconButton,
  Toolbar,
  Tooltip,
  TablePagination,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";

import { visuallyHidden } from "@mui/utils";
import * as XLSX from "xlsx";

import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import { getReferrersReportsAction } from "../../../../store/reducers/dashboardSlice";

interface DataRow {
  customer_name: string;
  referrer_name: string;
  lead_status: string;
  lead_created: string;
}

type Order = "asc" | "desc";

type TabKey = "lead" | "booking" | "financial";

const headCells: { id: keyof DataRow; label: string; numeric?: boolean }[] = [
  { id: "customer_name", label: "Customer Name" },
  { id: "referrer_name", label: "Referrer Name" },
  { id: "lead_status", label: "Lead Status" },
  { id: "lead_created", label: "Lead Created Date" },
];

function descendingComparator<T>(a: T, b: T, orderBy: keyof T): number {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

interface EnhancedTableHeadProps {
  order: Order;
  orderBy: keyof DataRow;
  onRequestSort: (event: MouseEvent<unknown>, property: keyof DataRow) => void;
}

function EnhancedTableHead({
  order,
  orderBy,
  onRequestSort,
}: EnhancedTableHeadProps) {
  const createSortHandler =
    (property: keyof DataRow) => (event: MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

interface ToolbarProps {
  filterText: string;
  onFilterTextChange: (text: string) => void;
  onDownload: () => void;
}

const EnhancedTableToolbar: React.FC<ToolbarProps> = ({
  filterText,
  onFilterTextChange,
  onDownload,
}) => (
  <Toolbar
    sx={{
      display: "flex",
      justifyContent: "space-between",
      backgroundColor: (theme) => alpha(theme.palette.common.white, 0.04),
      px: 2,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        bgcolor: "background.paper",
        borderRadius: 1,
        px: 1,
      }}
    >
      <SearchIcon sx={{ color: "text.secondary" }} />
      <InputBase
        placeholder="Search…"
        value={filterText}
        onChange={(e: ChangeEvent<HTMLInputElement>) =>
          onFilterTextChange(e.target.value)
        }
        sx={{ color: "inherit", ml: 1, flex: 1 }}
      />
    </Box>
    <Tooltip title="Download CSV">
      <IconButton onClick={onDownload} color="primary">
        <DownloadIcon />
      </IconButton>
    </Tooltip>
  </Toolbar>
);

const ReferrerReportList: React.FC<{ userProfile?: any }> = ({
  userProfile,
}) => {
  const { referrersReports } = useSelector((state: any) => state.dashboard);

  const dispatch = useDispatch();

  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof DataRow>("customer_name");
  const [filterText, setFilterText] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [tabValue, setTabValue] = useState<TabKey>("lead");

  const handleRequestSort = (
    event: MouseEvent<unknown>,
    property: keyof DataRow
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const filteredRows = useMemo(
    () =>
      referrersReports.filter((row: any) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(filterText.toLowerCase())
        )
      ),
    [filterText, referrersReports]
  );

  const sortedRows = useMemo(
    () => filteredRows.sort(getComparator(order, orderBy)),
    [filteredRows, order, orderBy]
  );

  const handleDownload = () => {
    const customData = sortedRows.map((row: any) => ({
      "Customer Name": row.customer_name,
      "Referrer Name": row.referrer_name,
      "Lead Status": row.lead_status,
      "Created Date": row.lead_created
        ? dayjs(row.lead_created).format("DD/MM/YYYY")
        : "N/A",
    }));
    const worksheet = XLSX.utils.json_to_sheet(customData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, `${tabValue}_reports.csv`);
  };

  const [filterFormData, setFilterFormData] = useState<any>({
    startDate: dayjs().subtract(30, "day").format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
  });

  useEffect(() => {
    dispatch(getReferrersReportsAction(filterFormData));
  }, [filterFormData, dispatch]);

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        bgcolor: "#f9f9f9",
        color: "#000",
        borderRadius: 2,
        boxShadow: 3,
      }}
    >
      <EnhancedTableToolbar
        filterText={filterText}
        onFilterTextChange={setFilterText}
        onDownload={handleDownload}
      />
      <TableContainer sx={{backgroundColor: '#E5F4F1'}}>
        <Table size="small">
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
          />
          <TableBody>
            {sortedRows
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row: any, index: any) => (
                <TableRow
                  hover
                  key={index}
                  sx={{
                    bgcolor:
                      index % 2 === 0 ? "background.default" : "grey.100",
                  }}
                >
                  <TableCell>{row.customer_name}</TableCell>
                  <TableCell>{row.referrer_name}</TableCell>
                  <TableCell>{row.lead_status}</TableCell>
                  <TableCell>
                    {row.lead_created
                      ? dayjs(row?.lead_created).format(
                              userProfile?.dateFormat
                                ? userProfile?.dateFormat
                                : "DD/MM/YYYY"
                            )
                      : "N/A"}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 50, 100, 200]}
        component="div"
        count={sortedRows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e: ChangeEvent<HTMLInputElement>) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Paper>
  );
};

export default ReferrerReportList;
